import {KpiWithValue} from '@hconnect/common/types'
import {useUserContext} from '@hconnect/login'
import {customThemeConstants, dataTestId} from '@hconnect/uikit'
import {Grid} from '@mui/material'
import {FC, useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FunctionsResponse, Variable} from '../../types'
import {generateDefaultFunction} from '../../utils'

import {FormulaFormCard} from './FormulaFormCard'
import {JanusKeyFiguresCard} from './JanusKeyFiguresCard'
import {KpiCard} from './KpiCard'
import {ThresholdCard} from './ThresholdCard'
import {VariableCard} from './VariableCard'

type KpiCalculationDetailsProps = {
  selectedKpi: KpiWithValue
  sensors: Variable[]
  functionsVariables: Variable[]
  functions?: FunctionsResponse
  isFetchingKpis?: boolean
}
export const KpiCalculationDetails: FC<KpiCalculationDetailsProps> = ({
  selectedKpi,
  functions,
  functionsVariables,
  sensors,
  isFetchingKpis
}) => {
  const {t} = useTranslation()
  const {loggedUser} = useUserContext()
  const userRoles = loggedUser?.hasRoles
  const canUpdateJanusKeyFigures =
    !!userRoles &&
    (userRoles.includes('HPRODUCE_CCC_SUPPORTER') ||
      userRoles.includes('HPRODUCE_PRODUCTION_MANAGER') ||
      userRoles.includes('HPRODUCE_PLANT_CONTROLLER'))
  const [insertFunctionHandler, setInsertFunctionHandler] = useState<
    ((fn: string) => void) | undefined
  >()
  const insertDefaultFunction = useCallback(
    (functionName: string) => {
      const functionDefinition = functions?.functionDefinitions.find(
        ({name}) => name === functionName
      )
      if (functionDefinition) insertFunctionHandler?.(generateDefaultFunction(functionDefinition))
    },
    [functions?.functionDefinitions, insertFunctionHandler]
  )

  return (
    <Grid container spacing={2} pt={0}>
      <Grid xs={12} pt={0}>
        <KpiCard kpi={selectedKpi?.definition} />
      </Grid>
      {canUpdateJanusKeyFigures && (
        <Grid xs={12} pt={0}>
          <JanusKeyFiguresCard kpi={selectedKpi?.definition} />
        </Grid>
      )}
      <Grid xs={12}>
        <FormulaFormCard
          showLoader={isFetchingKpis}
          functions={functions?.functionDefinitions}
          formula={selectedKpi.definition.formula}
          kpiId={selectedKpi.definition.kpiDefinitionId}
          lastResult={selectedKpi.lastResult}
          frequency={selectedKpi.definition.frequency}
          onRegisterInsertHandler={(handler) => setInsertFunctionHandler(() => handler)}
        />
      </Grid>
      <Grid xs={12} md={6}>
        <VariableCard
          variables={sensors}
          label={t('kpiCalculationSettings.label.insertSensor')}
          nameSx={{color: 'primary.light'}}
          {...dataTestId('sensor_card')}
        />
      </Grid>
      <Grid xs={12} md={6}>
        <VariableCard
          variables={functionsVariables}
          label={t('kpiCalculationSettings.label.insertFunction')}
          nameSx={{color: customThemeConstants().palette.lightChart1}}
          onClick={insertDefaultFunction}
          onMouseDown={(e) => e.preventDefault()}
          {...dataTestId('function_card')}
        />
      </Grid>
      <Grid xs={12}>
        <ThresholdCard
          thresholds={{
            highHigh: selectedKpi.definition.highHigh,
            high: selectedKpi.definition.high,
            low: selectedKpi.definition.low,
            lowLow: selectedKpi.definition.lowLow
          }}
          unit={selectedKpi.definition.unit}
          kpiId={selectedKpi.definition.kpiDefinitionId}
        />
      </Grid>
    </Grid>
  )
}
